import { useNavigate } from "react-router";
import { useExportFormats, useDeleteExportFormat, useCloneExportFormat } from "../hooks/useExportFormats";
import { toast } from "@/components/Toaster";
import { AdminListHeader } from "../../../components/admin/AdminListHeader";
import "../admin.css";

export function ExportFormatListPage() {
  const navigate = useNavigate();
  const { formats, isLoading, error, refresh } = useExportFormats();
  const { remove } = useDeleteExportFormat();
  const { clone } = useCloneExportFormat();

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
      refresh();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to delete";
      toast.error(message, { label: "Retry", onClick: () => handleDelete(id) });
    }
  };

  // R-201 — clone for the variations workflow: duplicate a format, then rename
  // its clone (the user lands on the list with the new "(copy)" row).
  const handleClone = async (id: string) => {
    try {
      await clone(id);
      refresh();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to clone";
      toast.error(message, { label: "Retry", onClick: () => handleClone(id) });
    }
  };

  return (
    <div className="admin-page">
      <AdminListHeader
        title="Export Formats"
        count={formats.length}
        primaryAction={{ label: "+ New Format", onClick: () => navigate("/admin/export-formats/new") }}
      />

      {isLoading && <div className="admin-loading"><div className="loading-spinner" /></div>}
      {error && <div className="admin-error">{error}</div>}

      <div className="jt-list">
        {formats.map((fmt) => (
          <div key={fmt.id} className="jt-card" onClick={() => navigate(`/admin/export-formats/${fmt.id}`)} style={{ cursor: "pointer" }}>
            <div className="jt-card-header">
              <h3 className="jt-card-name">{fmt.name}</h3>
              <span className="jt-badge">{fmt.formatType.replace("_", " ")}</span>
            </div>
            <div className="jt-card-meta">
              <span>{fmt.columns.length} columns</span>
              <span>Used by {fmt.usedByJobTypeCount ?? 0} Job {fmt.usedByJobTypeCount === 1 ? "Type" : "Types"}</span>
              <span>Updated {new Date(fmt.updatedAt).toLocaleDateString()}</span>
            </div>
            <div className="jt-card-actions">
              {/* R-27 — the card itself navigates to detail; a per-row Edit
                  button was a redundant second affordance. Delete + Clone remain
                  as distinct actions (must stopPropagation so they don't trigger
                  the card's navigate). */}
              <button className="rb-btn" onClick={(e) => { e.stopPropagation(); handleClone(fmt.id); }}>
                Clone
              </button>
              <button className="rb-btn rb-btn-danger" onClick={(e) => { e.stopPropagation(); handleDelete(fmt.id); }}>
                Delete
              </button>
            </div>
          </div>
        ))}

        {!isLoading && formats.length === 0 && (
          <div className="tab-placeholder">
            <span className="tab-placeholder-text">No export formats created yet</span>
            <button
              className="rb-btn rb-btn-save"
              style={{ marginTop: "var(--space-4)" }}
              onClick={() => navigate("/admin/export-formats/new")}
            >
              + Create Export Format
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
